import React, { useEffect } from "react";
import { Printer, X as XIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export const isTouchDevice = () => typeof window !== "undefined" && (window.matchMedia?.("(pointer: coarse)")?.matches || navigator.maxTouchPoints > 0);

/** Calls window.print() from a user gesture; falls back to a hint when the browser refuses. */
export const safePrint = () => {
  try {
    if (typeof window.print !== "function") throw new Error("no print");
    window.print();
    return true;
  } catch (e) {
    toast.error("Your browser blocked printing. Use the browser menu (Share → Print) instead.");
    return false;
  }
};

/**
 * Same-tab print overlay for a single label image. Avoids window.open (blocked
 * on iOS Safari / Android Chrome after an async fetch) — the print dialog is
 * fired directly from the tap on the Print button.
 */
export const LabelPrintOverlay = ({ src, title, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="label-print-overlay fixed inset-0 z-[100] bg-[#0B1B2B]/95 flex flex-col items-center justify-center p-4" data-testid="label-print-overlay">
      <div className="print:hidden flex items-center justify-between w-full max-w-md mb-3">
        <p className="text-sm text-[#F6F1E4] font-medium">{title || "Label"}</p>
        <button type="button" onClick={onClose} className="text-[#B9C8D5] hover:text-white p-1" data-testid="label-print-close"><XIcon className="w-5 h-5" /></button>
      </div>
      <div className="label-print-target bg-white rounded-lg p-3 max-w-md w-full">
        <img src={src} alt={title || "Label"} className="w-full h-auto" data-testid="label-print-image" />
      </div>
      <div className="print:hidden flex gap-2 mt-4 w-full max-w-md">
        <button type="button" onClick={safePrint} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#FFD166] text-[#0B1B2B] font-medium" data-testid="label-print-now">
          <Printer className="w-4 h-4" /> Print label
        </button>
        <a href={src} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-3 rounded-lg border border-[#FFD166]/40 text-[#FFD166]" data-testid="label-print-open">
          <ExternalLink className="w-4 h-4" /> Open
        </a>
      </div>
      <p className="print:hidden text-[11px] text-[#B9C8D5] mt-3 text-center max-w-md">On a phone, if nothing happens tap Open, then use your browser's Share → Print.</p>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .label-print-overlay, .label-print-overlay .label-print-target, .label-print-overlay .label-print-target * { visibility: visible !important; }
          .label-print-overlay { position: absolute !important; inset: 0 !important; background: #fff !important; padding: 0 !important; display: block !important; }
          .label-print-overlay .label-print-target { max-width: none !important; padding: 0 !important; border-radius: 0 !important; }
        }
      `}</style>
    </div>
  );
};

export default LabelPrintOverlay;
